import { getSgConnectEnvironment } from 'sg-dashboard-dashboard-common-code';
import { DatasetType, WidgetTemplateDto } from 'sg-dashboard-sdk';

export enum SgConnectEnvironment {
  Homologation = 'homologation',
  Production = 'production'
}

enum BootstrapVersion {
  Three, // BS3
  Four, // BS4
  Five // BS5
}

export const defaultKey = '155';
const envUrls: { [key: string]: string } = {
  dev: 'https://api-sg-dashboard-dev.fr.world.socgen',
  dev2: 'https://api-sg-dashboard-dev2.fr.world.socgen',
  uat: 'https://api-sg-dashboard-uat.fr.world.socgen',
  uat2: 'https://api-sg-dashboard-uat2.fr.world.socgen',
  prd: 'https://api-sg-dashboard.fr.world.socgen'
};

export interface UrlsOverride {
  'widget-metadata-url'?: string;
  'selector-metadata-url'?: string;
}

function validateEndpointUrl(endpoint: string | undefined, endpointType: string): string {
  const normalizedEndpoint = typeof endpoint === 'string' ? endpoint.trim() : '';
  if (normalizedEndpoint === '') {
    throw new Error(`urls-override.${endpointType} must be a non-empty string`);
  }

  const placeholderCount = (normalizedEndpoint.match(/\{identifier\}/g) || []).length;
  if (placeholderCount !== 1) {
    throw new Error(`urls-override.${endpointType} must contain exactly one {identifier} placeholder`);
  }

  try {
    new URL(normalizedEndpoint.replace('{identifier}', 'identifier'));
  } catch {
    throw new Error(`urls-override.${endpointType} must be a valid URL`);
  }

  return normalizedEndpoint;
}

function validateUrlsOverride(urlsOverride: UrlsOverride | null | undefined): UrlsOverride | undefined {
  if (urlsOverride == null) {
    return undefined;
  }
  if (typeof urlsOverride !== 'object' || Array.isArray(urlsOverride)) {
    throw new Error('urls-override must be an object');
  }

  const validatedUrlsOverride: UrlsOverride = {};
  for (const endpointType of Object.keys(urlsOverride)) {
    if (endpointType !== 'widget-metadata-url' && endpointType !== 'selector-metadata-url') {
      throw new Error(`Unsupported urls-override property: ${endpointType}`);
    }

    if (endpointType === 'widget-metadata-url') {
      validatedUrlsOverride['widget-metadata-url'] = validateEndpointUrl(
        urlsOverride['widget-metadata-url'],
        endpointType
      );
    } else {
      validatedUrlsOverride['selector-metadata-url'] = validateEndpointUrl(
        urlsOverride['selector-metadata-url'],
        endpointType
      );
    }
  }
  return validatedUrlsOverride;
}

export interface WidgetTemplate extends WidgetTemplateDto {
  official: boolean;
}

export interface SgWidgetParameters {
  key: string;
  tagName: string;
  type: any;
  outputEvent: string | null;
  inputs: any;
  inputsProperty: string | null;
  configuration: any;
  properties: any;
  attributes: any;
  shadowDomIncompatible: boolean;
  bootstrapVersion: BootstrapVersion;
  width: string;
  newProducer: boolean;
  hidden: boolean | null;
  logKeyAttribute: string | null;
  persistConfiguration: boolean;
  withPredecessorInConfig: boolean;
  templateIdentifier: string;
  dataSourceIdentifiers: string[];
}

export enum WidgetType {
  WIDGET = 'widget',
  SELECTOR = 'selector'
}

export function generateInputs(inputData: string | undefined): any {
  let parsedInputData: any = undefined;
  if (inputData != null) {
    try {
      parsedInputData = JSON.parse(inputData);
    } catch (err) {
      throw new Error(`Cannot stringify input-data ${inputData}`);
    }
  }
  return {
    seqNum: 0,
    predecessors: [{ __from__: 'GlobalSelector', ...(parsedInputData || {}) }]
  };
}

export function createWidgetInfoFromWidgetTemplate(
  widgetTemplate: WidgetTemplateDto,
  widgetType: string,
  inputData: string | undefined
): SgWidgetParameters {
  const outputEventKeys = widgetTemplate.webComponent.outputEvents
    ? Object.keys(widgetTemplate.webComponent.outputEvents)
    : [];
  const inputs = generateInputs(inputData);
  return {
    key: defaultKey,
    tagName: widgetTemplate.webComponent.identifier,
    type: widgetType,
    outputEvent: outputEventKeys.length ? outputEventKeys[0] : null,
    inputs,
    inputsProperty: widgetTemplate.webComponent.inputDataProperty || 'inputData',
    configuration: widgetTemplate.webComponent.userSelection,
    properties: widgetTemplate.webComponent.properties || {},
    attributes: widgetTemplate.webComponent.attributes || {},
    shadowDomIncompatible: !!widgetTemplate.webComponent.shadowDomIncompatible,
    bootstrapVersion: widgetTemplate.webComponent.bsVersion as any,
    width: 'auto',
    newProducer: widgetTemplate.webComponent.eventWhenInputProcessed || false,
    hidden: false,
    logKeyAttribute: widgetTemplate.webComponent.logKeyAttribute,
    persistConfiguration: false,
    withPredecessorInConfig: false,
    templateIdentifier: widgetTemplate.identifier,
    dataSourceIdentifiers: widgetTemplate.webComponent.dataSourceIdentifiers || []
  };
}

export function resolveDashboardBackUrl(environment: string): string {
  const sgConnectEnvironment: SgConnectEnvironment = getSgConnectEnvironment();
  const environmentKey = sgConnectEnvironment === SgConnectEnvironment.Production ? environment : `${environment}2`;
  const sgDashboardBackUrl = envUrls[environmentKey];
  if (sgDashboardBackUrl == null) {
    throw new Error('Unsupported type check sgm dashboard components attribute');
  }
  return sgDashboardBackUrl;
}

export function resolveMetadataUrl(
  environment: string,
  type: string,
  identifier: string,
  urlsOverride?: UrlsOverride
): string {
  if (type !== WidgetType.WIDGET && type !== WidgetType.SELECTOR) {
    throw new Error(`Unsupported attribute "type": ${type}`);
  }

  if (typeof identifier !== 'string' || identifier.length === 0) {
    throw new Error('Widget identifier must be a non-empty string');
  }

  const validatedUrlsOverride = validateUrlsOverride(urlsOverride);
  const configuredEndpoint =
    type === WidgetType.WIDGET
      ? validatedUrlsOverride && validatedUrlsOverride['widget-metadata-url']
      : validatedUrlsOverride && validatedUrlsOverride['selector-metadata-url'];
  const encodedIdentifier = encodeURIComponent(identifier);
  if (configuredEndpoint != null) {
    return configuredEndpoint.replace('{identifier}', encodedIdentifier);
  }
  const sgDashboardBackUrl = resolveDashboardBackUrl(environment);
  switch (type) {
    case WidgetType.SELECTOR:
      return `${sgDashboardBackUrl}/api/v2/selectors/identifier/${encodedIdentifier}`;
    case WidgetType.WIDGET:
      return `${sgDashboardBackUrl}/api/v3/widgets/identifier/${encodedIdentifier}`;
    default:
      throw new Error(
        `Unsupported  attribute "type" of widget only ${WidgetType.SELECTOR}, ${WidgetType.WIDGET} are supported`
      );
  }
}

export interface SourceCreateDto {
  identifier: string; // human-readable (e.g. c-profit, sg-dashboard, ...). must not be empty
  name: string;
  publicInfo: PublicInfoDto;
  component: SourceDtoComponent;
  official: boolean;
  publicDataSource: boolean;
  hidden: boolean;
  type: DatasetType;
}

export interface SourceDto extends SourceCreateDto {
  createdAt: string;
  updatedAt: string;
  userId: string;
  id: number;
  popularity: number;
  widgetsCount: number;
  boardsCount: number;
  sharedByUser: boolean;
  receivedByUser: boolean;
}

export interface PublicInfoDto {
  information: string;
  longInformationHtml: string | null;
  contacts: string | null;
  sources: string | null;
  tags: string | null;
}
// IMPORTED FROM BACKEND.TS maybe move backend.ts to SDK
export interface SourceDtoComponent {
  prerequisiteJs: string | null;
  dataUrl: string | null;
  dataToken: string;
  fieldsUrl: string | null;
  fieldsToken: string;
  fieldsDescription: string | null;
  confidentialityLevel: string;
  onlySgNetwork: boolean | null;
  scopes: string[];
}
